import { Box, Typography } from '@mui/material';
import { PermIdentity, QueryBuilder } from '@mui/icons-material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import RepeatIcon from '@mui/icons-material/Repeat';
import SubjectIcon from '@mui/icons-material/Subject';
import { AppointmentSlotDuration } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { ProviderMedicalRole } from '@uplift/types';
import React, { useCallback } from 'react';
import { AppointmentDurationMappingDurationMinutes, useCalendarEventPopoverActions } from '../../../../Calendar';
import { useFormContext } from '../AddSessionForm.types';
import { AddSessionStatusAlert } from '../components/AddSessionStatusAlert.component';
import { AppointmentDateInput } from '../components/AppointmentDateInput.component';
import { AppointmentRepetitionSelect } from '../components/AppointmentRepetitionSelect.components';
import { ExistingOrInviteToggle } from '../components/ExistingOrInviteToggle.component';
import { FormSection } from '../components/FormSection.component';
import { InvitationAlert } from '../components/InvitationAlert.components';
import { NotesInput } from '../components/NotesInput.component';
import { SlotDurationInput } from '../components/SlotDurationInput.component';
import { StartTimeSelect } from '../components/StartTimeSelect';
import { UserInfo } from '../components/UserInfo';
import { useSessionFormFieldsSync } from '../components/form.hooks';
import { useAvailableSlots } from '../hooks';
import { Hourglass } from '@uplift/icons';
import { AutoScrollToErrorField } from '../../../../Form';


export const useEffectOnUpdateValue: typeof React.useEffect = (callback, deps) => {
  const initialRef = React.useRef(true);

  return React.useEffect(() => {
    if (initialRef.current) {
      initialRef.current = false;
      return;
    }
    return callback();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};


export const AddSessionForm: React.FC = () => {
  const currentUser = useCurrentuser();
  const formikContext = useFormContext();
  const isPsychiatrist = currentUser.medicalRole === ProviderMedicalRole.psychiatrist;
  const isTherapist = currentUser.medicalRole === ProviderMedicalRole.therapist;
  const wrapperActions = useCalendarEventPopoverActions();
  const dateFormatted = React.useMemo(() => formikContext.values.date, [ formikContext.values.date ]);
  const appointmentDuration = React.useMemo(() => {
    if (formikContext.values.duration) {
      return AppointmentDurationMappingDurationMinutes[formikContext.values.duration];
    }

    return AppointmentDurationMappingDurationMinutes.sixty;
  }, [ formikContext.values.duration ]);

  const allAvailableSlots = useAvailableSlots(dateFormatted, appointmentDuration);

  useSessionFormFieldsSync();

  React.useEffect(() => {
    wrapperActions?.current?.updatePosition();
  }, [ formikContext.values.isNewPatient, wrapperActions, formikContext.errors, formikContext.touched ]);

  const handleInviteToggleChange = useCallback((isNewPatient: boolean) => {
    if (!isPsychiatrist) {
      return;
    }

    if (isNewPatient === formikContext.values.isNewPatient) {
      return;
    }

    if (isNewPatient) {
      formikContext.setFieldValue('duration', AppointmentSlotDuration.sixty);
    } else {
      formikContext.setFieldValue('duration', AppointmentSlotDuration.thirty);
    }
  }, [ formikContext, isPsychiatrist ]);

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <AutoScrollToErrorField />
      <AddSessionStatusAlert />

      <ExistingOrInviteToggle name="isNewPatient" onChange={handleInviteToggleChange}/>

      <InvitationAlert />

      <FormSection icon={PermIdentity} includeLabel={formikContext.values.isNewPatient}>
        <UserInfo />
      </FormSection>

      <FormSection icon={QueryBuilder}>
        <Box display="flex" flexDirection="column" gap="16px" width="100%">
          <Box display="flex">
            <AppointmentDateInput name="date" />
            <Box ml={1} minWidth="110px">
              <StartTimeSelect
                medicalRole={currentUser.medicalRole ?? ProviderMedicalRole.therapist}
                data-testid="start-time-select"
                name="startTime"
                appointmentDuration={appointmentDuration}
                slots={allAvailableSlots}
              />
            </Box>
          </Box>
        </Box>
      </FormSection>


      <FormSection icon={Hourglass} iconHeight="auto">
        <SlotDurationInput
          data-testid="duration-select"
          name="duration"
          startTime={formikContext.values.startTime}
          slots={allAvailableSlots}
          medicalRole={currentUser.medicalRole ?? ProviderMedicalRole.therapist}
        />
      </FormSection>

      {isTherapist && (
        <FormSection icon={RepeatIcon} iconHeight="auto">
          <AppointmentRepetitionSelect name="repeats" data-testid="repetition-select" />
        </FormSection>
      )}

      <FormSection icon={LocationOnOutlinedIcon} iconHeight="auto">
        <Box display="flex" alignItems="center" style={{ cursor: 'default', paddingTop: '2px' }}>
          <Typography variant="body1">
            Virtual (Video and Audio)
          </Typography>
        </Box>
      </FormSection>

      <FormSection icon={SubjectIcon} iconHeight="auto">
        <NotesInput name="notes" />
      </FormSection>
    </Box>
  );
};
